import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Radio, Send } from 'lucide-react';
import syncService from '../services/syncService';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/datetime';
import { apiErrorMessage } from '../utils/apiError';
import './OxiTopologyDiagram.css';
import './SyncConnectStatusReport.css';

// THE ON-SITE INSTALLATION, AS THE PROPERTY'S OWN SYNCCONNECT SEES IT.
//
// "Request status" queues STATUSREPORT; the client answers with its health page as JSON (V562) on its next poll,
// so the answer arrives on the client's schedule — the line under the title says which state it is in. What is
// drawn is the client's own verdict, checks and connection picture (Opera database / OXI polls us / OXI sends to
// us → SyncConnect → hconnect Hub, each hop coloured as the health page colours it). Nothing is re-judged here.
//
// Superadmins can ask every active SyncConnect project at once (sa-only).

const POLL_MS = 15000;

const VERDICT = {
    ok:       { cls: 'ok',   word: 'All checks passing' },
    degraded: { cls: 'warn', word: 'Running, but something has gone quiet' },
    starting: { cls: 'warn', word: 'Starting up — some checks have not run yet' },
    error:    { cls: 'bad',  word: 'Something is wrong' },
};

const CHECK_WORD = {
    ok: ['ok', 'OK'], error: ['bad', 'FAILED'], stale: ['warn', 'NO RECENT ACTIVITY'], 'n/a': ['na', 'NOT APPLICABLE'],
};

const Hop = ({ state, verb, word }) => (
    <div className={`scsr-hop scsr-hop-${state || 'na'}`}>
        {verb && <span className="scsr-hop-word">{verb}</span>}
        <span className="scsr-hop-line" />
        <span className="scsr-hop-word">{word}</span>
    </div>
);

const PathDiagram = ({ path }) => {
    if (!path) return null;
    const me = path.syncConnect || {};
    return (
        <div className="oxi-diagram">
            <div className="oxi-diagram-head">
                <span className="oxi-diagram-title">Connection path</span>
                <span className="oxi-diagram-mode oxi-mode-connect">as reported by SyncConnect</span>
            </div>
            <div className="scsr-flow">
                <div className="scsr-lanes">
                    {(path.sources || []).map((l, i) => (
                        <div key={i} className="scsr-lane">
                            <div className="oxi-node oxi-node-property">
                                <div className="oxi-node-title">{l.title}</div>
                                <div className="oxi-node-sub">{l.subtitle}</div>
                            </div>
                            <Hop state={l.state} verb={l.verb} word={l.word} />
                        </div>
                    ))}
                </div>
                <div className="oxi-node oxi-node-connect scsr-me">
                    <div className="oxi-node-title">{me.title || 'SyncConnect'}</div>
                    <div className="oxi-node-sub">{me.subtitle}</div>
                    <div className="oxi-node-host">
                        {me.machine || 'localhost'}<span className="oxi-node-port">:{me.port || '8081'}</span>
                    </div>
                </div>
                <Hop state={path.hubHop?.state} word={path.hubHop?.word} />
                <div className="oxi-node oxi-node-hub">
                    <div className="oxi-node-title">{path.hubNode?.title || 'hconnect Hub'}</div>
                    {path.hubNode?.host && <div className="oxi-node-host">{path.hubNode.host}</div>}
                </div>
            </div>
        </div>
    );
};

export default function SyncConnectStatusReport({ syncId }) {
    const { isSuperAdmin } = useAuth();
    const [state, setState] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [allResult, setAllResult] = useState(null);

    const load = useCallback(async () => {
        try {
            setState(await syncService.getSyncConnectStatusReport(syncId));
            setError(null);
        } catch (e) {
            setError(apiErrorMessage(e, 'The SyncConnect status could not be read'));
        }
    }, [syncId]);

    useEffect(() => { load(); }, [load]);

    // While a request is with the client, look again until its answer lands.
    useEffect(() => {
        if (!state?.waiting) return undefined;
        const t = setInterval(load, POLL_MS);
        return () => clearInterval(t);
    }, [state?.waiting, load]);

    const request = async () => {
        setBusy(true); setError(null);
        try {
            setState(await syncService.requestSyncConnectStatusReport(syncId));
        } catch (e) {
            setError(apiErrorMessage(e, 'The status could not be requested'));
        } finally {
            setBusy(false);
        }
    };

    const requestAll = async () => {
        setBusy(true); setError(null); setAllResult(null);
        try {
            setAllResult(await syncService.requestAllSyncConnectStatusReports());
            load();
        } catch (e) {
            setError(apiErrorMessage(e, 'The status could not be requested from every project'));
        } finally {
            setBusy(false);
        }
    };

    if (state && !state.usesSyncConnect) return null;

    const latest = state?.latest;
    const report = latest?.report;
    const verdict = VERDICT[latest?.overallStatus] || VERDICT.error;

    return (
        <section className="scsr">
            <div className="scsr-head">
                <h2 className="onsite-scope-title">SyncConnect installation status</h2>
                <div className="scsr-actions">
                    <button type="button" className="btn btn-primary btn-sm" onClick={request} disabled={busy}
                        title="Ask this property's SyncConnect for its health status">
                        <Radio size={14} /> {busy ? 'Asking…' : 'Request status'}
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={load} disabled={busy}>
                        <RefreshCw size={14} /> Reload
                    </button>
                    {isSuperAdmin && (
                        <button type="button" className="btn btn-secondary btn-sm sa-only" onClick={requestAll} disabled={busy}
                            title="Ask every active SyncConnect project for its status">
                            <Send size={14} /> Request from all SyncConnect projects
                        </button>
                    )}
                </div>
            </div>

            {allResult && (
                <div className="os-msg scsr-msg">
                    Requested from {allResult.queued} of {allResult.total} active SyncConnect project{allResult.total === 1 ? '' : 's'}.
                    {(allResult.results || []).filter(r => !r.queued).map(r => (
                        <div key={r.syncId}>Project {r.syncId}: {r.error}</div>
                    ))}
                </div>
            )}
            {error && <div className="os-msg os-msg-error scsr-msg">{error}</div>}

            {state?.waiting && (
                <div className="scsr-status scsr-status-wait">
                    Requested {formatDateTime(state.requestedAt)} · waiting for SyncConnect to collect it on its next poll
                </div>
            )}

            {!latest ? (
                <p className="onsite-empty">No status has been received from this property&rsquo;s SyncConnect yet. Press &ldquo;Request status&rdquo;.</p>
            ) : (
                <>
                    <div className={`scsr-banner scsr-banner-${verdict.cls}`}>
                        <strong>{report?.statusText || verdict.word}</strong>
                        <span className="scsr-banner-when">
                            Reported {formatDateTime(latest.receivedAt)}
                            {report?.serverTimeLocal && <> · property clock {report.serverTimeLocal}</>}
                        </span>
                    </div>

                    <div className="scsr-facts">
                        <span><b>Version</b> {report?.version || latest.clientVersion || '—'}</span>
                        <span><b>Mode</b> {report?.mode || '—'}</span>
                        <span><b>Resort</b> {report?.resort || '—'}</span>
                        <span><b>Server</b> {report?.machine || '—'}</span>
                        <span><b>Up since</b> {formatDateTime(report?.startedUtc)}</span>
                    </div>

                    <PathDiagram path={report?.connectionPath} />

                    <table className="onsite-summary-table scsr-checks">
                        <thead><tr><th /><th>Check</th><th>Result</th><th>When</th></tr></thead>
                        <tbody>
                            {(report?.checks || []).map(c => {
                                const [cls, word] = CHECK_WORD[c.status] || ['warn', 'WAITING'];
                                const when = c.checkedUtc
                                    ? (c.heartbeat ? `last seen ${c.ago}` : `checked ${c.ago}`)
                                    : (c.status === 'n/a' ? '' : 'not yet run');
                                return (
                                    <tr key={c.name} className={`scsr-row-${cls}`}>
                                        <td><span className="scsr-dot" /></td>
                                        <td>{c.label || c.name}{c.detail && <div className="scsr-detail">{c.detail}</div>}</td>
                                        <td className="scsr-word">{word}</td>
                                        <td className="scsr-when">{when}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}
        </section>
    );
}
